import { Box, Typography, Grid } from "@mui/material";
import dayjs from "dayjs";

import InfoField from "./infoField";

import type { Arrangements } from "../../../utils/types/call";

interface ArrangementDetailsProps {
  arrangements: Arrangements | null | undefined;
}

interface Field {
  label: string;
  value: string | null;
}

interface SectionItem {
  title: string;
  subtitle: string;
  fields: Field[][];
}

interface SectionConfig<T> {
  title: string;
  data: T[] | null | undefined;
  mapToItems: (item: T) => Omit<SectionItem, "title">;
}

const GridRow = ({ children }: { children: React.ReactNode }) => (
  <Grid container item spacing={1.5}>
    {children}
  </Grid>
);

const formatDate = (date: string | null | undefined): string | null =>
  date ? dayjs(date).format("MM/DD/YYYY") : null;

const Section = ({ title, items }: { title: string; items: SectionItem[] }) => (
  <Box mb={2}>
    <Typography
      variant="subtitle1"
      fontWeight={600}
      mb={1}
      overflow="hidden"
      textOverflow="ellipsis"
      whiteSpace="nowrap"
    >
      {title}
    </Typography>
    {items.map((item, index) => (
      <Box
        key={index}
        mb={1}
        p={1}
        borderRadius={1}
        sx={{
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography
          variant="subtitle2"
          mb={0.5}
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {item.subtitle}
        </Typography>
        <Grid container spacing={1} mb={1}>
          {item.fields.map((row, rowIndex) => (
            <GridRow key={rowIndex}>
              {row.map((field, fieldIndex) => (
                <Grid item xs={6} key={fieldIndex}>
                  <InfoField label={field.label} value={field.value} />
                </Grid>
              ))}
            </GridRow>
          ))}
        </Grid>
      </Box>
    ))}
  </Box>
);

const ArrangementDetails = ({ arrangements }: ArrangementDetailsProps) => {
  const sections: SectionConfig<any>[] = [
    {
      title: "Deliveries",
      data: arrangements?.deliveries,
      mapToItems: (delivery) => ({
        subtitle:
          !delivery?.delivery_type && !delivery?.vendor
            ? "Unknown"
            : `${delivery.delivery_type || "Unknown"} - ${delivery.vendor || "Unknown"}`,
        fields: [
          [
            { label: "Berth", value: delivery?.berth },
            {
              label: "Pallets",
              value: delivery?.pallet_count?.toString() ?? null,
            },
          ],
          [
            { label: "Date", value: formatDate(delivery?.delivery_date) },
            { label: "Launch", value: delivery?.launch },
          ],
        ],
      }),
    },
    {
      title: "Bunkers / Fresh Water",
      data: arrangements?.bunkers,
      mapToItems: (bunker) => ({
        subtitle:
          !bunker?.type && !bunker?.supplier
            ? "Unknown"
            : `${bunker.type || "Unknown"} - ${bunker.supplier || "Unknown"}`,
        fields: [
          [
            { label: "Berth", value: bunker?.berth },
            { label: "MTS", value: bunker?.mts?.toString() ?? null },
          ],
          [
            { label: "Date", value: formatDate(bunker?.delivery_date) },
            { label: "Oil Type", value: bunker?.oil_type },
          ],
        ],
      }),
    },
    {
      title: "Onboard Services",
      data: arrangements?.onboardServices,
      mapToItems: (service) => ({
        subtitle:
          !service?.typeOfService && service?.vendor
            ? "Unknown"
            : `${service.typeOfService || "Unknown"} - ${service.vendor || "Unknown"}`,
        fields: [
          [
            { label: "Berth", value: service?.berth },
            { label: "Completed", value: formatDate(service?.date_completed) },
          ],
        ],
      }),
    },
  ];

  const hasData = sections.some((section) => (section?.data?.length ?? 0) > 0);

  return (
    <Box>
      {sections.map(({ title, data, mapToItems }) =>
        data?.length ? (
          <Section
            key={title}
            title={title}
            items={data.map((item) => ({ title, ...mapToItems(item) }))}
          />
        ) : null
      )}

      {!hasData && (
        <Typography variant="body2" color="text.secondary" textAlign="center">
          No arrangements found
        </Typography>
      )}
    </Box>
  );
};

export default ArrangementDetails;
